import cn from 'classnames';
import { useCallback, useEffect, useState, useContext, useRef } from 'react';
import {
  Link,
  useNavigate,
  useParams,
  useSearchParams,
} from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { MessagesContext } from './contexts/MessagesContext';
import { ThreatEditModal } from '../ThreatsPage/ThreatEditModal';
import streamSaver from 'streamsaver';

import { ampli } from "../../ampli";
import { API, ShareAPI } from '../../API';
import Modal from '../../components/Modal';
import {ReactComponent as AIIcon} from '../../assets/ai-white.svg';
import { Loader } from '../../components/Loader/Loader';
import Tooltip from '../../components/Tooltip/Tooltip';
import { Tooltip as ReactTooltip } from 'react-tooltip';
import ExportReportPopup from '../../components/ExportReportPopup';
import { useCurrentUser } from '../../contexts/CurrentUser';
import CopyNarrativeToWorkspacePopup from '../../components/CopyNarrativeToWorkspacePopup';
import { ReactComponent as ArrowIcon } from './assets/arrow.svg';
import { ReactComponent as ArrowDownIcon } from './assets/arrowDown.svg';
import { ReactComponent as EditIcon } from '../../assets/edit.svg';
import { ReactComponent as ApproveIcon } from './assets/approve.svg';
import { ReactComponent as MailIcon } from './assets/mail.svg';
import { ReactComponent as DeleteIcon } from '../../assets/delete.svg';
import { ReactComponent as ShareIcon } from '../ReportPage/assets/share.svg';
import { ReactComponent as MessagesIcon } from './assets/messages.svg';
import { ReactComponent as ReportIcon } from './assets/report.svg';
import { ReactComponent as SaveIcon} from '../../assets/save.svg';
import { ReactComponent as HistoryIcon } from './assets/history.svg';
import { ReactComponent as ChevronDown} from '../SearchPage/assets/chevron-down.svg';
import { ReactComponent as SourcesAnalysisIcon } from './assets/sources_analysis.svg';
import { ReactComponent as StoriesIcon } from './assets/stories.svg';
import { ReactComponent as SortIcon } from '../../assets/sort.svg';
import { ReactComponent as TableIcon } from '../../assets/Table.svg';
import { ReactComponent as BlocksIcon } from '../../assets/Blocks.svg';
import { ReactComponent as MergeIcon } from './assets/merge.svg';
import { ReactComponent as CopyIcon } from '../../assets/copy.svg';
import { ReactComponent as DistributionIcon } from '../../assets/distribution.svg';
import { ReactComponent as ShareThreatIcon } from '../../assets/shareThreat.svg';
import { ReactComponent as AddToIcon } from '../../assets/addTo.svg';
import { ReactComponent as CloseIcon } from '../SearchPage/assets/close.svg';
import {ReactComponent as LinkIcon} from '../../assets/link.svg';
import { ReactComponent as AnchorIcon } from '../../assets/anchor.svg';
import { AdjustmentSuggestions } from './AdjustmentSuggestion/AdjustmentSuggestions';
import { Tabs } from '../../components/Tabs/Tabs';
import { NarrativeTreeChart } from './NarrativeTreeChart';
import { NarrativeStories } from './NarrativeStories/NarrativeStories';
import { Distribution } from './Distribution';
import {ExtendedMessageView} from '../../components/MessagesTable/ExtendedMessageView';
import { DropdownMenu } from '../../components/DropdownMenu/DropdownMenu';
import { Radiobutton } from '../../components/DropdownMenu/Radiobutton';
import { SortingOrder } from '../../components/DropdownMenu/SortingOrder';
import { NarrativeEditHistory } from '../NarrativeEditHistoryPage/NarrativeEditHistory';
import { Filter } from './Filter/Filter';
import styles from './NarrativePage.module.scss';
import {QueryInput} from "../NarrativeAddPage/QueryInput/QueryInput";
import { NearMixedPairWarning } from '../../components/NearMixedPairWarning/NearMixedPairWarning';
import {Calendar} from "../SearchPage/Calendar/Calendar";
import { AddToNarrativeModal } from '../../components/AddToNarrativeModal/AddToNarrativeModal';
import { BreakdownsWrapper } from './Breakdowns/BreakdownsWrapper';
import { cutString } from '../../utils/cutString';
import websocketConnection from '../../websocket';
import { CaseStatusbar } from './CaseStatusBar';
import { extractPlatformName } from '../../utils/extractPlatformName';
import { AddToSourceGroupModal } from '../../components/AddToSourceGroupModal/AddToSourceGroupModal';
import {toast} from "react-toastify";
import { showRequestError } from '../../utils/requestError';
import {LoaderSmall} from "../../components/LoaderSmall/LoaderSmall";
import { StoryContent } from '../../components/StoryContent/StoryContent';
import { contentTypesMapping } from '../../utils/contentTypes';
import { mapThreatsStatus, mapThreatsTypesConst, mapThreatsSubjects } from '../../utils/threats';
import { TopicReportPageNew } from '../TopicReportPage/TopicReportPageNew';
import { PopulationProgressBar } from './PopulationProgressBar';
import { AmazonDistribution } from './AmazonDistribution';
import DatePickerWrapper from '../../components/Datepicker/DatepickerWrapper';
import { CaseParametersCard } from './CaseParametersCard';
import { useCopilotPage } from '../../components/CopilotSwitch/CopilotSwitch';
import { CaseWindmillAppsMenu } from '../../components/WindmillEmbedded/CaseWindmillAppsMenu';

const mapThreatStatusClasses = {
  ACCEPTED: styles.accepted,
  PENDING: styles.pending,
  DECLINED: styles.declined,
};

export function NarrativePage({ isShare = false }) {
  const { t } = useTranslation();
  const [currentUser] = useCurrentUser();
  const {
    narrative,
    messages,
    selectedMessages,
    deleteMessages,
    patchMessages,
    deduplication,
    toggleDeduplication,
    clearSelectedMessages,
    toggleTab,
    clearAllSelectedMessages,
    applySearch,
    getMessages,
    dispatch,
    fetchNarrative,
    getStories,
    error,
    areAllMessagesSelected,
    toggleSelectAllMessagesInCase,
    handleAllMessages,
    cancelTranslateAll,
    isSearchApplied,
    isLoading,
  } = useContext(MessagesContext);

  const initStartDate = null;
  const initEndDate = null;

  const workspaceConfig = currentUser?.workspace?.config;
  const featureFlags = workspaceConfig?.featureflags;
  const isAdvancedSyntax = workspaceConfig?.keywords_parser_class === 'Workspace16ESKeywordsQuery';
  const hideCommentsByDefault = featureFlags?.includes('hide_comments_by_default');
  const showPopulationStatus = featureFlags?.includes('show_population_status');
  const showStories = featureFlags?.includes('show_stories');

  const defaultContentTypes = hideCommentsByDefault ? Object.keys(contentTypesMapping).filter(key => key !== 'COMMENT') : [];

  // Search block
  const [activeLoadMoreButton, setActiveLoadMoreButton] = useState(false);
  const [exportModal, setExportModal] = useState({ isActive: false, id: null });
  const [startDate, setStartDate] = useState(initStartDate);
  const [endDate, setEndDate] = useState(initEndDate);
  const [sentiment, setSentiment] = useState([]);
  const [platforms, setPlatforms] = useState([]);
  const [sourceGroups, setSourceGroups] = useState([]);
  const [feeds, setFeeds] = useState([]);
  const [languages, setLanguages] = useState([]);
  const [contentTypes, setContentTypes] = useState(defaultContentTypes);
  const [countries, setCountries] = useState([]);
  const [cursor, setCursor] = useState(null);
  const [sources, setSources] = useState([]);
  const [searchQuery, setSearchQuery] = useState(null);
  const [showOnlyBots, setShowOnlyBots] = useState(false);
  const [discreditations, setDiscreditations] = useState([]);
  const [affiliationCountries, setAffiliationCountries] = useState([]);
  const [affiliationTypes, setAffiliationTypes] = useState([]);
  const [affiliationTypesExclude, setAffiliationTypesExclude] = useState(false);
  const [languagesExclude, setLanguagesExclude] = useState(false);
  const [countriesExclude, setCountriesExclude] = useState(false);
  const [sourceGroupsExclude, setSourceGroupsExclude] = useState(false);
  const [feedsExclude, setFeedsExclude] = useState(false);
  const [sourcesExclude, setSourcesExclude] = useState(false);
  const [affiliationCountriesExclude, setAffiliationCountriesExclude] = useState(false);
  const [geoGroup, setGeoGroup] = useState([]);
  const [geoGroupExclude, setGeoGroupExclude] = useState(false);
  const [tags, setTags] = useState([]);
  const [tagsExclude, setTagsExclude] = useState(false);
  const [botsExclude, setBotsExclude] = useState(false);
  const [discreditationsExclude, setDiscreditationsExclude] = useState(false);
  const queryInputRef = useRef(null);
  const keywordsRef = useRef(null);
  const [isValidQuery, setIsValidQuery] = useState(true);
  const [hasMixedNearPair, setHasMixedNearPair] = useState(false);
  const onKeywordsChange = useCallback((value, isValid) => {
    keywordsRef.current = value;
    if(isValid !== null) {
      setIsValidQuery(isValid);
    }
  }, []);

  const api = isShare ? ShareAPI : API;

  const [messagesStatus, setMessagesStatus] = useState(null);
  const [showOnlyFavorites, setShowOnlyFavorites] = useState(false);

  const [threatName, setThreatName] = useState(null);
  const [threatDescription, setThreatDescription] = useState(null);
  const [incidentType, setIncidentType] = useState(null);
  const [topics, setTopics] = useState(null);

  const [tabIndex, setTabIndex] = useState(0);
  const [messageModeIndex, setMessageModeIndex] = useState(1);
  const selectedMessagesKeys = Object.keys(selectedMessages);
  const [deleteConfirmModal, setDeleteConfirmModal] = useState({
    isActive: false,
    narrative: null,
    status: null,
    ids: null,
  });
  const [approveConfirmModal, setApproveConfirmModal] = useState({
    isActive: false,
    narrative: null,
    status: null,
    ids: null,
  });
  const [copyNarrativeModal, setCopyNarrativeModal] = useState({
    isActive: false,
    workspaceId: null,
  });

  const [saveAsNarrativeModal, setSaveAsNarrativeModal] = useState({isOpen: false, threat: narrative})
  const [saveAsThreatModal, setSaveAsThreatModal] = useState({isOpen: false, narrative: narrative});

  const navigate = useNavigate();
  const { narrativeId, threatId } = useParams();
  const [searchParams] = useSearchParams();
  const [sorting, setSorting] = useState('-date_publicated');
  const [dropdownMenu, setDropdownMenu] = useState(false);
  const [isExportMenuOpen, setIsExportMenuOpen] = useState(false);
  const [errorModal, setErrorModal] = useState(false);
  const [editThreatModal, setEditThreatModal] = useState({isOpen: false, threat: null});
  const [addToNarrativeModal, setAddToNarrativeModal] = useState(false);
  const [addToThreatModal, setAddToThreatModal] = useState(false);
  const [addToSourceGroupMadal, setAddToSourceGroupModal] = useState(false);
  const [mergeStoriesModal, setMergeStoriesModal] = useState({
    isActive: false,
    story_ids: [],
    new_summary: '',
    isLoading: false,
    error: null,
  });

  const [page, setPage] = useState(1);
  const [narrativeStatus, setNarrativeStatus] = useState(narrative?.impact_assessment_processing_metadata?.status);

  useEffect(() => {
    applySearch([
      platforms,
      sourceGroups,
      languages,
      sources,
      countries,
      sentiment,
      contentTypes,
      showOnlyBots,
      discreditations,
      affiliationCountries,
      feeds,
      geoGroup,
      tags,
      languagesExclude,
      countriesExclude,
      sourceGroupsExclude,
      sourcesExclude,
      affiliationCountriesExclude,
      feedsExclude,
      geoGroupExclude,
      discreditationsExclude,
      searchQuery !== null && searchQuery?.trim().length > 0,
      startDate,
      endDate,
    ].some((value) => Boolean(value) && !(Array.isArray(value) && value.length === 0)));
  }, [
    platforms,
    sourceGroups,
    languages,
    sources,
    countries,
    sentiment,
    contentTypes,
    showOnlyBots,
    discreditations,
    affiliationCountries,
    feeds,
    languagesExclude,
    countriesExclude,
    sourceGroupsExclude,
    sourcesExclude,
    affiliationCountriesExclude,
    feedsExclude,
    searchQuery,
    geoGroup,
    geoGroupExclude,
    discreditationsExclude,
    tags,
    startDate,
    endDate,
  ])

  useEffect(() => {
    if (narrative?.type !== 'IMPACT_ASSESSMENT') {
      return;
    }

    const handleImpactAssessmentProgress = (data) => {
      if (data?.object_id !== narrative?.id) {
        return;
      }

      try {
        setNarrativeStatus(data?.data.status)

        if(data?.data.status === 'DONE') {
          let id = narrativeId || threatId;
          fetchNarrative(id)
        }
      } catch {}
    };

    websocketConnection.addHandler('IMPACT_ASSESSMENT', handleImpactAssessmentProgress);

    return () => {
      websocketConnection.removeHandler('IMPACT_ASSESSMENT');
    };
  }, [narrative]);

  const getPopulationBucketKey = (casePopulationProgress) => {
    if (casePopulationProgress === 100) {
      return '100';
    } else if (casePopulationProgress >= 75) {
      return '75';
    } else if (casePopulationProgress >= 50) {
      return '50';
    } else if (casePopulationProgress >= 25) {
      return '25';
    } else if (casePopulationProgress >= 1) {
      return '1';
    } else if (casePopulationProgress === 0) {
      return '0';
    } else {
      return '0';
    }
  };

  let populationProgressKey = `${getPopulationBucketKey(narrative?.population_status?.case_population_progress)}__${narrative?.population_status?.sentiment_status}`;

  useEffect(() => {
    if (narrative?.type === 'IMPACT_ASSESSMENT') {
      return;
    }

    const handleCasePopulationProgress = (data) => {
      if (data?.object_id !== narrative?.id) {
        return;
      }

      try {
        dispatch({
          type: 'PATCH_NARRATIVE_POPULATION_STATUS',
          payload: data?.data
        });

      } catch {}
    };

    websocketConnection.addHandler('NARRATIVE_POPULATION_STATUS', handleCasePopulationProgress);

    return () => {
      websocketConnection.removeHandler('NARRATIVE_POPULATION_STATUS');
    }
  }, [narrative]);

  useEffect(() => {
    let id = narrativeId || threatId;
    fetchNarrative(id)
  }, [narrativeId, threatId])

  // The copilot lives app-wide; this page only tells it which case is on
  // screen and whether the case qualifies for a chat.
  useCopilotPage({
    narrativeId: narrative?.id,
    title: narrative?.name,
    eligible:
      !isShare &&
      ((narrative?.parameters?.create_stories && narrative?.population_status?.stories_calculation_status === 'DONE') ||
        (narrative?.type === 'TOPIC_MODELING' && narrative?.population_status?.tm_calculation_status === 'DONE')),
  });

  useEffect(() => {
    if(narrative?.type !== "IMPACT_ASSESSMENT") {
      return
    }
    setNarrativeStatus(narrative?.impact_assessment_processing_metadata?.status)
  }, [narrative])


  const buttonsOnScroll = () => {
    if(Object.keys(selectedMessages).length > 0) {
      const buttons = document.getElementById('bulk');
      let scrolly = window.pageYOffset;
      if (!buttons) {
        return
      }

      if(scrolly > 150) {
        buttons.classList.add(styles.floating)
      } else {
        buttons.classList.remove(styles.floating)
      }
    } else {
      return
    }
  }

  useEffect(() => {
      window.addEventListener('scroll', buttonsOnScroll);
      window.addEventListener('mousemove', buttonsOnScroll);

    return() => {
      window.removeEventListener('scroll', buttonsOnScroll);
      window.removeEventListener('mousemove', buttonsOnScroll);
    }
  })
   

  const saveFile = (blob, format) => {
    console.log(blob);
    // Create blob link to download
    const url = window.URL.createObjectURL(new Blob([blob]));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute(
      'download',
      `${narrative.name}_${new Date().toISOString()}.${format}`,
    );

    // Append to html link element page
    document.body.appendChild(link);

    // Start download
    link.click();

    // Clean up and remove the link
    link.parentNode.removeChild(link);
  }

  const handleExport = (format, narrativeId, sortingQuery, searchQuery) => {
    const urlParams = new URLSearchParams();
    urlParams.set('narrative_id', narrativeId);

    if (narrative.parameters?.start_date) {
      urlParams.set(
        'start',
        narrative.parameters.start_date.split('T')[0] + 'T00:00:00',
      );
    }
    if (narrative.parameters?.end_date) {
      urlParams.set(
        'end',
        narrative.parameters.end_date.split('T')[0] + 'T23:59:59'
      );
    }

    if (sortingQuery) {
      urlParams.set('sorting', sortingQuery);
    }

    ampli.track({
      event_type: 'Export narrative',
      event_properties: {
        user_id: currentUser?.id,
        workspace_id: currentUser?.workspace_id,
        narrative_id: narrativeId,
        format: format,
      }
    });

    api.fetchBlob(
      'GET',
      `/API/v1/messages/export/${format}?${urlParams.toString()}`,
    ).then((blob) => {
      if (!(blob instanceof Blob)) {
        toast.error(t('Export failed. Please try again later.'));
        return;
      }
      saveFile(blob, format);
    });
  };

  const handleAsyncExport = (format, narrativeId) => {
    API.fetch('POST', `/API/v1/async-export`, null, {
      'type': 'case',
      'object_id': narrativeId,
      'output_format': format,
    }).then((data) => {
      const Msg = () => (
        <div>
          {t('An export of') + ` ${t('export_type_of_' + data.type)} id=${data.object_id} `}
          <Link to={`/exports?object_id=${data.object_id}&types=${data.type}`}>
            {t(`export_status_${data.status}`).toLowerCase()}
          </Link>
        </div>
      );

      const displayMsg = () => {
        toast.success(<Msg />);
      };
      displayMsg();
    });
  };

  const handleExportStream = async (format, narrativeId, sortingQuery, searchQuery) => {
    const urlParams = new URLSearchParams();
    urlParams.set('narrative_id', narrativeId);

    if (narrative.parameters?.start_date) {
      urlParams.set(
        'start',
        narrative.parameters.start_date.split('T')[0] + 'T00:00:00',
      );
    }
    if (narrative.parameters?.end_date) {
      urlParams.set(
        'end',
        narrative.parameters.end_date.split('T')[0] + 'T23:59:59'
      );
    }

    if (sortingQuery) {
      urlParams.set('sorting', sortingQuery);
    }

    try {
      const response = await api.Fetch(
        'GET',
        `/API/v1/messages/export/${format}?${urlParams.toString()}`,
      )

      const filename = `${narrative.name}_${new Date().toISOString()}.${format}`;

      if (!response.ok) {
        throw new Error(`Failed to download the file. Status: ${response.status} - ${response.statusText}`);
      }

      const contentLength = response.headers.get('Content-Length');

      const fileStream = streamSaver.createWriteStream(filename, {
        size: parseInt(contentLength),
      });

      const writer = fileStream.getWriter();

      const reader = response.body.getReader();

      while (true) {
        const { done, value } = await reader.read();
        if (done) {
          writer.close();
          break;
        }
        writer.write(value);
      }

      console.log('File download completed successfully!');
    } catch (error) {
      console.error('Failed to download the file:', error);
    }
  };

  useEffect(() => {
    if (isShare) {
      return
    }

    if (!narrative?.is_threat) {
      ampli.track({
        event_type: 'Open case',
        event_properties: {
          user_id: currentUser?.id,
          workspace_id: currentUser?.workspace_id,
          narrative_id: narrative?.id,
          is_stories_applied: narrative?.parameters?.create_stories,
          is_aspect_based_sentiment_applied: narrative?.parameters?.aspect_based_sentiment,
        }
      });
    }

    if(narrative) {
      if(narrative?.is_threat && narrative?.review_status === 'ACCEPTED' && (narrative.read === false || narrative.read === null)) {
        api.fetch('POST', `/API/v1/threats/${narrative.id}/read/`, null).then((data) => {
          return
        });
      }
    } else {
      return
    }

    if (narrative.is_threat && narrative.review_status === 'ACCEPTED') {
      ampli.track({
        event_type: 'Open threat',
        event_properties: {
          user_id: currentUser?.id,
          workspace_id: currentUser?.workspace_id,
          threat_id: narrative.id,
        }
      });
    }

  }, [narrative])


  const checkNarrative = () => {
    if (!narrative) {
      return;
    }

    const urlParams = new URLSearchParams();
    urlParams.set('narrative_id', narrative.id);
    urlParams.set('message_status', 'READ');
    urlParams.set('size', 5);

    return api.fetch('GET', `/API/v1/messages?${urlParams.toString()}`)
  };


  const sortingMenu = [
    {
      name: t('Newest publications'),
      value: '-date_publicated',
    },
    {
      name: t('Oldest publications'),
      value: 'date_publicated',
    },
    {
      name: t('Most viewed'),
      value: '-impressions',
    },
    {
      name: t('Highest engagement'),
      value: '-engagement',
    },
    
  ];

  const sortingMenuStories = [
    {
      name: t('Most publications'), 
      value: '-messages_count'
    },
    {
      name: t('Oldest publications'),
      value: 'first_date',
    },
    {
      name: t('Newest publications'),
      value: '-last_date',
    },
    {
      name: t('Highest engagement'),
      value: '-engagement_sum',
    },
    {
      name: t('Most High-Risk accounts'),
      value: '-high_risk_accounts_count',
    },
    {
      name: t('Most viewed'),
      value: '-impressions_sum',
    }
  ]
  

  if(!narrative && error) {
    return <div className='noData'>{t(error)}</div>
  }

  if (!narrative) {
    return <Loader />;
  }


  const searchPanel = (
    <>
    <div className={'narrative-search-form-wrapper ' + styles.searchWrapper}>
      <QueryInput
        onMixedNearPairChange={setHasMixedNearPair}
        isShare={isShare}
        isAdvanced={isAdvancedSyntax}
        search={true}
        ref={queryInputRef}
        className={styles.queryInput}
        onChange={onKeywordsChange}
        isInvalid={!isValidQuery}
        clear={() => {
          dispatch({
            type: 'BULK_TRANSLATION_LOADING',
            isBulkTranslationLoading: false,
          });
          dispatch({ type: 'IS_BULK_TRANSLATION', isBulkTranslation: false });
          if (searchQuery) {
            keywordsRef.current = null;
            setSearchQuery(null);
            setCursor(null);
            setPage(1);
            applySearch(false);
            if (deduplication) {
              getStories(
                narrative,
                searchQuery,
                startDate,
                endDate,
                messagesStatus,
                sorting,
                1,
              );
            } else {
              getMessages(
                narrative,
                searchQuery,
                sorting,
                messagesStatus,
                deduplication,
                1,
                showOnlyFavorites,
                startDate,
                endDate,
                platforms,
                sourceGroups,
                isValidQuery,
                languages,
                sources,
                countries,
                sentiment,
                contentTypes,
                showOnlyBots,
                discreditations,
                affiliationCountries,
                feeds,
                geoGroup,
                languagesExclude,
                countriesExclude,
                sourceGroupsExclude,
                sourcesExclude,
                affiliationCountriesExclude,
                feedsExclude,
                geoGroupExclude,
                botsExclude,
                discreditationsExclude,
                tags,
                tagsExclude,
                null,
                isLoading,
                isSearchApplied,
                undefined,
                affiliationTypes,
                affiliationTypesExclude,
              );
            }
          } else {
            if (deduplication) {
              getStories(
                narrative,
                searchQuery,
                startDate,
                endDate,
                messagesStatus,
                sorting,
                page,
              );
            } else {
              getMessages(
                narrative,
                searchQuery,
                sorting,
                messagesStatus,
                deduplication,
                page,
                showOnlyFavorites,
                startDate,
                endDate,
                platforms,
                sourceGroups,
                isValidQuery,
                languages,
                sources,
                countries,
                sentiment,
                contentTypes,
                showOnlyBots,
                discreditations,
                affiliationCountries,
                feeds,
                geoGroup,
                languagesExclude,
                countriesExclude,
                sourceGroupsExclude,
                sourcesExclude,
                affiliationCountriesExclude,
                feedsExclude,
                geoGroupExclude,
                botsExclude,
                discreditationsExclude,
                tags,
                tagsExclude,
                null, // cursor was just reset above; the prop still holds the stale one
                isLoading,
                isSearchApplied,
                undefined,
                affiliationTypes,
                affiliationTypesExclude,
              );
            }
          }
        }}
        onKeyDown={(event) => {
          if (event.key === 'Enter' && event.shiftKey === false) {
            event.preventDefault();
            setActiveLoadMoreButton(false);
            setSearchQuery(keywordsRef.current);
          }
        }}
        onClick={(e) => {
          e.preventDefault();
          setActiveLoadMoreButton(false);
          setSearchQuery(keywordsRef.current);
        }}
      />

      <div className={styles.calendarWrapper}>
        <DatePickerWrapper
          onChange={(startDate, endDate) => {
            setStartDate(startDate);
            setEndDate(endDate);
            setCursor(null);
            setPage(1);
            if (keywordsRef?.current) {
              setSearchQuery(keywordsRef?.current);
            }
          }}
          initStartDate={startDate}
          initEndDate={endDate}
          isRange={true}
          buttonDateFormat="dd/MM/yy"
        />
      </div>

      <DropdownMenu
        isOpen={dropdownMenu}
        header={t('Sort by')}
        onClick={() => setDropdownMenu(!dropdownMenu)}
        buttonName={t('Sort')}
        icon={<SortIcon />}
      >
        <Radiobutton
          itemsList={deduplication ? sortingMenuStories : sortingMenu}
          current={sorting}
          onChange={(value) => setSorting(value) }
        />

      </DropdownMenu>
      <Filter
        isThreat={narrative.is_threat}
        isShare={isShare}
        narrativeId={narrative.id}
        platformsFilter={platforms}
        sourceGroupsFilter={sourceGroups}
        feedsFilter={feeds}
        languagesFilter={languages}
        sourcesFilter={sources}
        countriesFilter={countries}
        sentimentFilter={sentiment}
        contentTypesFilter={contentTypes}
        messagesStatusFilter={messagesStatus}
        showOnlyFavoritesFilter={showOnlyFavorites}
        showOnlyBotsFilter={showOnlyBots}
        discreditationsFilter={discreditations}
        affiliationCountriesFilter={affiliationCountries}
        affiliationTypesFilter={affiliationTypes}
        affiliationTypesExcludeFilter={affiliationTypesExclude}
        languagesExcludeFilter={languagesExclude}
        countriesExcludeFilter={countriesExclude}
        sourceGroupsExcludeFilter={sourceGroupsExclude}
        feedsExcludeFilter={feedsExclude}
        sourcesExcludeFilter={sourcesExclude}
        affiliationCountriesExcludeFilter={affiliationCountriesExclude}
        geoGroupExcludeFilter={geoGroupExclude}
        geoGroupFilter={geoGroup}
        botsExcludeFilter={botsExclude}
        discreditationsExcludeFilter={discreditationsExclude}
        tagsFilter={tags}
        tagsExcludeFilter={tagsExclude}
        onChange={(
          platformsList,
          sourceGroupsList,
          languages,
          sources,
          countries,
          sentiment,
          contentTypes,
          messagesStatus,
          showFavorites,
          showOnlyBots,
          discreditations,
          affiliationCountries,
          feeds,
          languagesExclude,
          countriesExclude,
          sourceGroupsExclude,
          sourcesExclude,
          affiliationCountriesExclude,
          feedsExclude,
          geoGroup,
          geoGroupExclude,
          botsExclude,
          discreditationsExclude,
          tagsArg,
          tagsExcludeArg,
          newAffiliationTypes,
          newAffiliationTypesExclude,
        ) => {
          setPlatforms(platformsList);
          setSourceGroups(sourceGroupsList);
          setLanguages(languages);
          setSources(sources);
          setCountries(countries);
          setSentiment(sentiment);
          setContentTypes(contentTypes);
          setMessagesStatus(messagesStatus);
          setShowOnlyFavorites(showFavorites);
          setShowOnlyBots(showOnlyBots);
          setDiscreditations(discreditations);
          setAffiliationCountries(affiliationCountries);
          setFeeds(feeds);
          setGeoGroup(geoGroup);
          setLanguagesExclude(languagesExclude);
          setCountriesExclude(countriesExclude);
          setSourceGroupsExclude(sourceGroupsExclude);
          setSourcesExclude(sourcesExclude);
          setAffiliationCountriesExclude(affiliationCountriesExclude);
          setFeedsExclude(feedsExclude);
          setGeoGroupExclude(geoGroupExclude);
          setDiscreditationsExclude(discreditationsExclude);
          setAffiliationTypes(newAffiliationTypes || []);
          setAffiliationTypesExclude(newAffiliationTypesExclude || false);
          if (keywordsRef?.current) {
            setSearchQuery(keywordsRef?.current);
          }
          setBotsExclude(botsExclude);
          setTags(tagsArg || []);
          setTagsExclude(tagsExcludeArg || false);
        }}
      />
    </div>
      {hasMixedNearPair ? (
        <NearMixedPairWarning
          className={styles.nearMixedPairWarning}
          startDate={startDate}
          isAdvanced={isAdvancedSyntax}
        />
      ) : null}
    </>
  );

  const isOpinions = (
    (narrative?.type === "TOPIC_MODELING" && narrative?.parameters?.topic_modeling_entity === 'OPINIONS_ONLY') ||
    narrative?.type === 'IMPACT_ASSESSMENT'
  )

  const tabslist = [
    {
      title: t('Overview'),
      icon: <DistributionIcon />,
      content: (
        <Distribution
          narrative={narrative}
          isShare={isShare}
          key={populationProgressKey}
          sentimentStatus={narrative?.population_status?.sentiment_status}
        />
      )
    },
    {
      title: t('Publications'),
      icon: <MessagesIcon />,
      content: (
        <ExtendedMessageView 
          narrative={narrative} 
          searchQuery={searchQuery}
          sorting={sorting}
          page={page}
          platforms={platforms}
          sourceGroups={sourceGroups}
          languages={languages}
          sources={sources}
          countries={countries}
          statuses={messagesStatus}
          start={startDate}
          end={endDate}
          sentiment={sentiment}
          contentTypes={contentTypes}
          cursor={cursor}
          setCursor={setCursor}
          setPage={setPage}
          isShare={isShare}
          showOnlyFavorites={showOnlyFavorites}
          showOnlyBots={showOnlyBots}
          discreditations={discreditations}
          affiliationCountries={affiliationCountries}
          affiliationTypes={affiliationTypes}
          affiliationTypesExclude={affiliationTypesExclude}
          feeds={feeds}
          geoGroup={geoGroup}
          tags={tags}
          tagsExclude={tagsExclude}
          languagesExclude={languagesExclude}
          countriesExclude={countriesExclude}
          sourceGroupsExclude={sourceGroupsExclude}
          sourcesExclude={sourcesExclude}
          affiliationCountriesExclude={affiliationCountriesExclude}
          feedsExclude={feedsExclude}
          geoGroupExclude={geoGroupExclude}
          botsExclude={botsExclude}
          discreditationsExclude={discreditationsExclude}
          approveModal={(data) =>
              setApproveConfirmModal(data)
            }

          deleteModal={
            (data) =>
            setDeleteConfirmModal(data)
          }
          searchPanel={searchPanel}
        />
      ),
    }
  ];

  if(narrative?.id === 58692) {
    tabslist.shift();
    tabslist.unshift(
      {
        title: t('Overview'),
        icon: <DistributionIcon />,
        content: (
          <AmazonDistribution
            narrative={narrative}
            isShare={isShare}
            key={populationProgressKey}
            sentimentStatus={narrative?.population_status?.sentiment_status}
          />
        )
      },
    )
  }

  const isNotIncidentOrTM = !["INCIDENT", "TOPIC_MODELING"].includes(narrative?.type);

  if (
    (showStories && (isNotIncidentOrTM || isOpinions) && !isShare) || (isNotIncidentOrTM && isShare && narrative?.parameters?.create_stories)
  ) {
    tabslist.push(
      {
        title: isOpinions ? t('Opinions') : t('Stories'),
        icon: <StoriesIcon />,
        content: (
          <NarrativeStories
            narrative={narrative}
            searchQuery={searchQuery}
            sorting={sorting}
            statuses={messagesStatus}
            showOnlyFavorites={showOnlyFavorites}
            page={page}
            setPage={setPage}
            start_date={startDate}
            end_date={endDate}
            platforms={platforms}
            sourceGroups={sourceGroups}
            languages={languages}
            sources={sources}
            countries={countries}
            searchPanel={searchPanel}
            cursor={cursor}
            setCursor={setCursor}
            approveModal={(data) =>
              setApproveConfirmModal(data)
            }
            deleteModal={(data) =>
              setDeleteConfirmModal(data)
            }
            isOpinions={isOpinions}
            isShare={isShare}
          />
        )
      }
    )
  }

  if(((narrative?.type === "TOPIC_MODELING" && narrative?.parameters?.topic_modeling_entity !== 'OPINIONS_ONLY') && narrative?.is_manual === false) && narrative.is_threat === false) {
    tabslist.push(
      {
        title: t('Topics'),
        icon: <StoriesIcon />,
        content: (
          <TopicReportPageNew isShare={isShare}/>
        )
      }
    )
  }

  if(narrative?.type !== 'IMPACT_ASSESSMENT') {
    tabslist.push(
      {
        title:  t('Actors'),
        icon: <ReportIcon />,
        content: <BreakdownsWrapper isShare={isShare} narrative={narrative} key={populationProgressKey}/>
      }
    )
  }

  if(narrative?.is_threat === false && !isShare) {
    tabslist.push(
      {
        'title': t('Edit history'),
        icon: <HistoryIcon />,
        content: <NarrativeEditHistory narrativeId={narrativeId || threatId} />,
      }
    );
  }

  const handleSaveAsThreat = ({
    narrative,
    threatName,
    threatDescription,
    caseType,
    incidentType,
    topics,
    geoScope,
    narratives,
    campaigns,
    counterEntities,
    reviewStatus,
    declineReason,
    organizations,
    isCreatedByUcbiReporter,
    threatPublicNameEn,
    threatPublicNameUk,
    threatPublicDescriptionEn,
    threatPublicDescriptionUk,
    threatIsPublic}) => {
    let parameters = null;
    if (narrative.parameters !== null) {
      parameters = {
        keywords: narrative.parameters.keywords || null,
        examples: narrative.parameters.examples || null,
        start_date: narrative.parameters.start_date || null,
        end_date: narrative.parameters.end_date || null,
        apply_supervised_classifier: narrative.parameters.apply_supervised_classifier,
        source_types: narrative.parameters.source_types || [],
        source_group_ids: narrative.parameters.source_group_ids || [],
        disable_filters: narrative.parameters.disable_filters,
        languages: narrative.parameters.languages || [],
        source_ids: narrative.parameters.source_ids || [],
        source_origin_country_ids: narrative.parameters.source_origin_country_ids || [],
        sentiment: narrative.parameters.sentiment || [],
        translate_keywords_query: narrative.parameters.translate_keywords_query,
        keywords_query_origin_language: narrative.parameters.keywords_query_origin_language || '',
        content_types: narrative.parameters.content_types || [],
        similarity_threshold: narrative.parameters.similarity_threshold || null,
        source_state_affiliated_all_countries: narrative.parameters.source_state_affiliated_all_countries || null,
        source_state_affiliated_country_ids: narrative.parameters.source_state_affiliated_country_ids || [],
        source_discreditated_entity_types: narrative.parameters.ource_discreditated_entity_types || [],
        source_bots: narrative.parameters.source_bots || null,
        exclusive_filters: narrative.parameters.exclusive_filters || null,
        country_group_ids: narrative.parameters.country_group_ids || [],
      }
    }

    api.fetch('POST', `/API/v1/narratives/${narrative.id}/copies`, null, {
      name: threatName,
      is_public: threatIsPublic,
      public_name_en: threatPublicNameEn,
      public_name_uk: threatPublicNameUk,
      public_description_en: threatPublicDescriptionEn,
      public_description_uk: threatPublicDescriptionUk,
      description: threatDescription,
      is_active: narrative.is_active,
      type: caseType?.value,
      is_threat: true,
      workspace_ids: [1],
      incident_type: incidentType?.map(v => v.value),
      topic: topics?.map(v => v.value),
      geo_scope: geoScope?.map(v => v.value),
      narrative_ids: narratives?.map(v => v.value),
      campaign_ids: campaigns?.map(v => v.value),
      counter_entity_ids: counterEntities?.map(v => v.value),
      parameters: parameters,
      adjustment_suggestions: null,
      is_created_by_ucbi_reporter: isCreatedByUcbiReporter,
    }).then((data) => {
      ampli.track({
        event_type: 'Share as a threat',
        event_properties: {
          user_id: currentUser?.id,
          workspace_id: currentUser?.workspace_id,
          narrative_id: narrative.id,
        }
      });

      window.open(`/threats/${data.id}`, '_blank');
    });
  }

  const saveAsNarrative = (threat) => {
    let parameters = null;

    if (threat.parameters !== null) {
      parameters = {
        keywords: threat.parameters.keywords || null,
        examples: threat.parameters.examples || null,
        start_date: threat.parameters.start_date || null,
        end_date: threat.parameters.end_date || null,
        apply_supervised_classifier: threat.parameters.apply_supervised_classifier,
        source_types: threat.parameters.source_types || [],
        source_group_ids: threat.parameters.source_group_ids || [],
        disable_filters: threat.parameters.disable_filters,
        languages: threat.parameters.languages || [],
        source_ids: threat.parameters.source_ids || [],
        source_origin_country_ids: threat.parameters.source_origin_country_ids || [],
        sentiment: threat.parameters.sentiment || [],
        translate_keywords_query: threat.parameters.translate_keywords_query,
        keywords_query_origin_language: threat.parameters.keywords_query_origin_language || '',
        content_types: threat.parameters.content_types || [],
        similarity_threshold: threat.parameters.similarity_threshold || null,
        source_state_affiliated_all_countries: narrative.parameters.source_state_affiliated_all_countries || null,
        source_state_affiliated_country_ids: narrative.parameters.source_state_affiliated_country_ids || [],
        source_discreditated_entity_types: narrative.parameters.ource_discreditated_entity_types || [],
        source_bots: narrative.parameters.source_bots || null,
        exclusive_filters: narrative.parameters.exclusive_filters || null,
        country_group_ids: threat.parameters.country_group_ids || [],
      }
    }

    api.fetch('POST', `/API/v1/narratives/${threat.id}/copies`, null, {
      name: threat.name,
      description: threat.description,
      is_active: threat.is_active,
      type: 'NARRATIVE',
      is_threat: false,
      is_manual: threat.parameters === null,
      incident_type: threat.incident_type,
      topic: threat.topic,
      parameters: parameters,
      adjustment_suggestions: null,
    }).then((data) => {
      ampli.track({
        event_type: 'Save threat',
        event_properties: {
          user_id: currentUser?.id,
          workspace_id: currentUser?.workspace_id,
          threat_id: threat.id,
        }
      });

      toast.success(t('Case created from threat'));
      navigate('/narratives');
    }).catch((e) => showRequestError(e, t('Save as narrative')));
  }

  const patchThreat = ({
    threat,
    threatName,
    threatDescription,
    caseType,
    incidentType,
    topics,
    geoScope,
    narratives,
    campaigns,
    counterEntities,
    reviewStatus,
    declineReason,
    organizations,
    isCreatedByUcbiReporter,
    threatPublicNameEn,
    threatPublicNameUk,
    threatPublicDescriptionEn,
    threatPublicDescriptionUk,
    threatIsPublic,
    workspaceId}) => {
    let data = {
      name: threatName,
      description: threatDescription,
      is_public: threatIsPublic,
      public_name_en: threatPublicNameEn,
      public_name_uk: threatPublicNameUk,
      public_description_en: threatPublicDescriptionEn,
      public_description_uk: threatPublicDescriptionUk,
    };

    const headers = { 'Content-Type': 'application/json' };

    if(workspaceId) {
      headers['workspace-id'] = workspaceId;
    }
    
    if(caseType) {
      data.type = caseType.value;
    }

    if(incidentType) {
      data.incident_type = incidentType.map(v => v.value);
    }

    if(topics) {
      data.topic = topics.map(v => v.value);
    }

    if(geoScope) {
      data.geo_scope = geoScope.map(v => v.value);
    }

    if(narratives) {
      data.narrative_ids = narratives.map(v => v.value);
    }

    if(campaigns) {
      data.campaign_ids = campaigns.map(v => v.value);
    }

    if(counterEntities) {
      data.counter_entity_ids = counterEntities.map(v => v.value);
    }

    if(reviewStatus) {
      data.review_status = reviewStatus;
    }

    if(declineReason) {
      data.decline_reason = declineReason;
    }

    if(organizations) {
      data.workspace_ids = organizations.map( v => v.value);
    }

    data.is_created_by_ucbi_reporter = isCreatedByUcbiReporter

    api.fetch('PATCH', `/API/v1/narratives/${threat.id}`, headers, data).then((data) => {
      toast.success(t('Threat updated'));
      fetchNarrative(data.id)
    }).catch((e) => showRequestError(e, t('Edit threat')));
  };

  const saveToThreat = (
    narrativeId,
    threatName,
    threatDescription,
    caseType,
    incidentType,
    topics,
    geoScope,
    narratives,
    campaigns,
    counterEntities,
    reviewStatus,
    declineReason,
    organizations,
    isCreatedByUcbiReporter,
    threatPublicNameEn,
    threatPublicNameUk,
    threatPublicDescriptionEn,
    threatPublicDescriptionUk,
    threatIsPublic) => {
    
    const messagesList = Object.keys(selectedMessages);
    
    let data = {
      name: threatName,
      description: threatDescription,
      is_threat: true,
      is_public: threatIsPublic,
      public_name_en: threatPublicNameEn,
      public_name_uk: threatPublicNameUk,
      public_description_en: threatPublicDescriptionEn,
      public_description_uk: threatPublicDescriptionUk,
    };

    const headers = { 'Content-Type': 'application/json' };
    
    if(caseType) {
      data.type = caseType.value;
    }

    if(incidentType) {
      data.incident_type = incidentType.map((v) => v.value);
    }

    if(topics) {
      data.topic = topics.map(v => v.value);
    }

    if(geoScope) {
      data.geo_scope = geoScope.map(v => v.value);
    }

    if(narratives) {
      data.narrative_ids = narratives.map(v => v.value);
    }

    if(campaigns) {
      data.campaign_ids = campaigns.map(v => v.value);
    }

    if(counterEntities) {
      data.counter_entity_ids = counterEntities.map(v => v.value);
    }

    if(reviewStatus) {
      data.review_status = reviewStatus;
    }

    if(declineReason) {
      data.decline_reason = declineReason;
    }

    if(organizations) {
      data.workspace_ids = organizations.map( v => v.value);
    }

    data.is_created_by_ucbi_reporter = isCreatedByUcbiReporter

    let body = {
      new_narrative: data,
      existing_narrative_id: null,
      story_ids: messagesList,
    }

    API.fetch('POST', `/API/v1/narratives/${narrativeId}/copy_from_stories`, null, body)
      .then(() => toast.success(t('Added to threat')))
      .catch((e) => showRequestError(e, t('Add to threat')));
  }


  const isUcbiUser = (
    currentUser?.workspace?.config?.is_ucbi_admin
    || currentUser?.workspace?.config?.is_reporter_only_user
  );

  let isNarrativeActive = narrative.is_active === true ? 'active' : 'inactive'
                
  let caseType = null;

  if(narrative?.type === "NARRATIVE" && narrative.is_manual === true) {
    caseType = t('Manual')
  } else if (narrative?.type === "NARRATIVE") {
    caseType = t('Narrative')
  } else if (narrative?.type === "INCIDENT") {
    caseType = t('Incident')
  } else if (narrative?.type === "IMPACT_ASSESSMENT") {
    caseType = t('Impact assessment')
  } else if (narrative.type === "TOPIC_MODELING") {
    caseType = t('Topic modeling')
  }

  const mapStatusClasses = {
    'active': styles.active,
    'inactive': styles.inactive
  }

  const mapPopulationStatus = {
    'POPULATING': t('Processing'),
    'DONE': t('Completed'),
    'FAILED': t('Failed'),
    'NO_NEEDED': '-',
  }

  const getThreatName = (threat) => {
    if (!threat?.is_public) {
      return threat?.name
    }

    let public_name;
    if(
      localStorage.getItem('i18nextLng') === 'ua-UK' ||
      localStorage.getItem('i18nextLng') === 'ru-RU' ||
      localStorage.getItem('i18nextLng') === 'ru' ||
      localStorage.getItem('i18nextLng') === 'uk'
    ) {
      public_name = threat?.public_name_uk;
    } else {
      public_name = threat?.public_name_en;
    }

    return public_name ?? threat?.name
  }

  const caseName = narrative.is_threat ? getThreatName(narrative) : narrative.name;

  const selectAllModalMessage = () => {
    toast.info(t('The process may take some time. Please wait a couple of minutes, then refresh the page to see the changes.'));
  }

  // Single "active process" view model for the sticky progress bar.
  // Import (initial population) wins while it is running; otherwise a manual
  // update (type === 'MANUAL' or is_manual) is shown as an indeterminate bar,
  // because backend cannot estimate its duration and sends eta === null.
  const getActivePopulationProcess = (populationStatusData) => {
    if (!populationStatusData) {
      return null;
    }
    const isSettled = (value) => !value || ['DONE', 'NO_NEEDED'].includes(value);
    // The import bar only makes sense for automatically populated cases.
    const canShowImport =
      !['IMPACT_ASSESSMENT', 'MANUAL'].includes(narrative?.type) &&
      !narrative?.is_threat &&
      !narrative?.is_manual;

    if (canShowImport && !isSettled(populationStatusData.case_population_status)) {
      return {
        type: 'IMPORT',
        status: populationStatusData.case_population_status,
        progress: populationStatusData.case_population_progress,
        eta: populationStatusData.case_population_eta_seconds,
        label: 'Importing publications:',
        hint: 'Reload page to see imported content',
        indeterminate: false,
      };
    }

    if (!isSettled(populationStatusData.case_manual_updating_status)) {
      return {
        type: 'MANUAL_UPDATE',
        status: populationStatusData.case_manual_updating_status,
        progress: null,
        eta: null,
        label: 'Adding publications to the case',
        hint: 'Reload page to see added publications',
        indeterminate: true,
      };
    }

    return null;
  };

  const activePopulationProcess = getActivePopulationProcess(narrative?.population_status);

  const mapInactiveNarrativeReason = {
    'Trial expired': t('Workspace trial was ended'),
    'Expired case': t('No activity for over 30 days'),
    'Insufficient AI quota': t('AI quota was exhausted'),
    'Insufficient ANALYTICAL quota': t('Analytical quota was exhausted'),
    'Manual': t('Deactivated by user'),
  }

  return (
    <div className="list-content">
      {activePopulationProcess ? (
        <div className={styles.progressBarWrapperSticky}>
          <PopulationProgressBar
            value={activePopulationProcess.progress}
            max={100}
            label={activePopulationProcess.label}
            hint={activePopulationProcess.hint}
            eta={activePopulationProcess.eta}
            status={activePopulationProcess.status}
            indeterminate={activePopulationProcess.indeterminate}
          />
        </div>
      ) : (
        ''
      )}

      <div className="page-header" style={{ paddingTop: activePopulationProcess ? '1rem' : '' }}>
        {!isShare && <div className="breadcrumb">
          <span>
            {currentUser?.workspace?.config?.is_reporter_only_user &&
            narrative.is_threat ? (
              <Link to="/threats">{t('Instances')}</Link>
            ) : isShare ? (
              t('Threats')
            ) : narrative.is_threat ? (
              <Link to="/threats">{t('Threats')}</Link>
            ) : (
              <Link to="/narratives">{t('Cases')}</Link>
            )}
          </span>
          <span>{cutString(caseName, 60)}</span>
        </div>}
        <div className="controls">
          {isShare ? (
            ''
          ) : (
            <>
              {!narrative.is_threat && narrative?.id !== 58692 ? (
                <Tooltip
                  content={
                    isUcbiUser
                      ? t('Share as an instance')
                      : t('Share as a threat')
                  }
                  position="bottom"
                >
                  <button
                    type="button"
                    className={cn('new-button')}
                    disabled={
                      narrative?.type === 'IMPACT_ASSESSMENT' &&
                      narrativeStatus !== 'DONE'
                    }
                    onClick={() => {
                      let isEnoughMessages = true;
                      checkNarrative().then((data) => {
                        if (data.total >= 3) {
                          isEnoughMessages = true;
                        }

                        if (isEnoughMessages === false) {
                          setErrorModal(true);
                        } else {
                          setThreatName(narrative.name);
                          setThreatDescription(narrative.description);
                          setSaveAsThreatModal({
                            isOpen: true,
                            narrative: narrative,
                          });
                        }
                      });
                    }}
                  >
                    <ShareThreatIcon />
                    {/* {t('Share as a threat')} */}
                  </button>
                </Tooltip>
              ) : (
                ''
              )}
              {currentUser?.is_super_admin === true &&
              narrative?.id !== 58692 &&
              !narrative.is_threat ? (
                <Tooltip content={t('Copy')} position="bottom">
                  <button
                    type="button"
                    className={cn('new-button')}
                    disabled={
                      narrative?.type === 'IMPACT_ASSESSMENT' &&
                      narrativeStatus !== 'DONE'
                    }
                    onClick={() =>
                      setCopyNarrativeModal({
                        isActive: true,
                      })
                    }
                  >
                    <CopyIcon />
                    {/* {t('Copy')} */}
                  </button>
                </Tooltip>
              ) : (
                ''
              )}
              {narrative.is_threat &&
              narrative?.id !== 58692 &&
              !currentUser?.workspace?.config?.is_reporter_only_user ? (
                <Tooltip content={t('Save as narrative')} position="bottom">
                  <button
                    type="button"
                    className={cn('new-button')}
                    onClick={() =>
                      setSaveAsNarrativeModal({
                        isOpen: true,
                        threat: narrative,
                      })
                    }
                  >
                    <SaveIcon />
                    {/* {t('Save')} */}
                  </button>
                </Tooltip>
              ) : (
                ''
              )}
              {!narrative.is_threat && narrative?.id !== 58692 ? (
                <Tooltip content={t('Edit')} position="bottom">
                  <Link
                    to={`/narratives/${narrative.id}/edit`}
                    className={cn(styles.buttonText, 'button', 'new-button')}
                  >
                    <EditIcon />
                    {/* {t('Edit')} */}
                  </Link>
                </Tooltip>
              ) : (
                ''
              )}
              {/* <Tooltip content={t('Edit history')} position='bottom'>
                <Link
                  to={`/narratives/${narrative.id}/edit/history`}
                  className={cn(styles.buttonText, 'button', 'new-button')}
                >
                  <HistoryIcon />
                </Link>
              </Tooltip> */}
              {narrative?.id !== 58692 &&
              ((narrative.is_threat && narrative.review_status === 'PENDING') ||
                (narrative.is_threat &&
                  (currentUser?.is_super_admin === true ||
                    currentUser?.workspace?.config?.is_ucbi_admin))) ? (
                <Tooltip content={t('Edit')} position="bottom">
                  <button
                    className={cn(styles.buttonText, 'button', 'new-button')}
                    onClick={() =>
                      setEditThreatModal({ isOpen: true, threat: narrative })
                    }
                    disabled={
                      narrative?.type === 'IMPACT_ASSESSMENT' &&
                      narrativeStatus !== 'DONE'
                    }
                  >
                    <EditIcon />
                    {/* {t('Edit')} */}
                  </button>
                </Tooltip>
              ) : (
                ''
              )}
              {narrative?.id === 58692 ? (
                <Tooltip content={t('AI Report')} position="bottom">
                  <a
                    type="button"
                    href={`/export_report/`}
                    className="button new-button"
                    disabled={
                      narrative?.type === 'IMPACT_ASSESSMENT' &&
                      narrativeStatus !== 'DONE'
                    }
                  >
                    <AIIcon />
                    Report
                    {/* {t('Share')} */}
                  </a>
                </Tooltip>
              ) : (
                <Tooltip content={t('Share')} position="bottom">
                  <button
                    type="button"
                    className="button new-button"
                    disabled={
                      narrative?.type === 'IMPACT_ASSESSMENT' &&
                      narrativeStatus !== 'DONE'
                    }
                    onClick={() =>
                      setExportModal({ isActive: true, id: narrative.id })
                    }
                  >
                    <ShareIcon />
                    {/* {t('Share')} */}
                  </button>
                </Tooltip>
              )}
              {narrative?.id !== 58692 ? (
                <DropdownMenu
                  isOpen={isExportMenuOpen}
                  header={t('Format')}
                  onClick={() => setIsExportMenuOpen(!isExportMenuOpen)}
                  buttonName={t('Export')}
                  icon={<ChevronDown />}
                  menuClassName="export"
                  disabled={
                    narrative?.type === 'IMPACT_ASSESSMENT' &&
                    narrativeStatus !== 'DONE'
                  }
                >
                  <ul>
                    <li
                      type="li"
                      className="new-li"
                      onClick={() => {
                        setIsExportMenuOpen(false);
                        // handleExport('csv', narrative.id, sortingQuery, searchQuery);
                        handleAsyncExport('csv', narrative.id);
                      }}
                    >
                      {t('CSV')}
                    </li>
                    <li
                      type="li"
                      className="new-li"
                      onClick={() => {
                        setIsExportMenuOpen(false);
                        // handleExport('json', narrative.id, sortingQuery, searchQuery);
                        handleAsyncExport('jsonl', narrative.id);
                      }}
                    >
                      {t('JSON')}
                    </li>
                    <li
                      type="li"
                      className="new-li"
                      onClick={() => {
                        setIsExportMenuOpen(false);
                        // handleExport('xml', narrative.id, sortingQuery, searchQuery);
                        handleAsyncExport('xml', narrative.id);
                      }}
                    >
                      {t('XML')}
                    </li>
                    <li
                      type="li"
                      className="new-li"
                      onClick={() => {
                        setIsExportMenuOpen(false);
                        handleExport(
                          'docx',
                          narrative.id,
                          sorting,
                          searchQuery,
                        );
                      }}
                    >
                      {t('DOCX')}
                    </li>
                  </ul>
                </DropdownMenu>
              ) : (
                ''
              )}
              {narrative?.id ? (
                <CaseWindmillAppsMenu caseId={narrative.id} />
              ) : null}
            </>
          )}
        </div>
      </div>
      <div className="next-card-header">
        <div className="row">
          <h2>{''}</h2>
          {Object.keys(selectedMessages).length > 0 ||
          areAllMessagesSelected ? (
            <div className={styles.bulkButtonsWrapper} id="bulk">
              <button>
                {areAllMessagesSelected
                  ? messages?.total
                  : Object.keys(selectedMessages).length}{' '}
                {t('selected')}{' '}
                <span
                  className={styles.clear}
                  onClick={() => {
                    clearAllSelectedMessages();
                    toggleSelectAllMessagesInCase(false);
                  }}
                >
                  <CloseIcon />
                </span>{' '}
              </button>
              {deduplication ? (
                ''
              ) : (
                <button onClick={() => setAddToSourceGroupModal(true)}>
                  <AddToIcon />
                  {t('Add to actor group')}
                </button>
              )}
              <button onClick={() => setAddToNarrativeModal(true)}>
                <AddToIcon />
                {t('Add to case')}
              </button>
              {deduplication ? (
                <button onClick={() => setAddToThreatModal(true)}>
                  <ShareThreatIcon />
                  {t('Add to threat')}
                </button>
              ) : (
                ''
              )}
              {deduplication ? (
                <button
                  onClick={() =>
                    setMergeStoriesModal({
                      isActive: true,
                      story_ids: selectedMessagesKeys,
                      new_summary:
                        messages?.objects
                          ?.filter((message) =>
                            selectedMessagesKeys.includes(
                              message.id.toString(),
                            ),
                          )
                          .sort(
                            (a, b) => b.messages_count - a.messages_count,
                          )?.[0]?.summary || '',
                      isLoading: false,
                      error: null,
                    })
                  }
                >
                  <MergeIcon />
                  {t('Merge')}
                </button>
              ) : (
                ''
              )}
              <button
                onClick={() =>
                  setApproveConfirmModal({
                    isActive: true,
                    narrative: narrative,
                    status: 'READ',
                    ids: selectedMessagesKeys,
                  })
                }
              >
                <ApproveIcon />
                {t('Approve')}
              </button>
              <button
                onClick={() =>
                  setApproveConfirmModal({
                    isActive: true,
                    narrative: narrative,
                    status: 'NEW',
                    ids: selectedMessagesKeys,
                  })
                }
              >
                <MailIcon />
                {t('Mark as unread')}
              </button>
              <button
                onClick={() =>
                  setDeleteConfirmModal({
                    isActive: true,
                    narrative: narrative,
                    status: 'NEW',
                    ids: selectedMessagesKeys,
                  })
                }
              >
                <DeleteIcon />
                {t('Delete')}
              </button>
            </div>
          ) : (
            ''
          )}
        </div>
      </div>

      <AdjustmentSuggestions
        narrative={narrative}
        messages={messages}
        onSuggestionApply={() => fetchNarrative(narrative.id)}
      />

      <CaseParametersCard
        narrative={narrative}
        isShare={isShare}
        showPopulationStatus={showPopulationStatus}
      />

      {activePopulationProcess?.type === 'IMPORT' && activePopulationProcess.eta === null ? (
        <div className={styles.noPopulationEstimate}>{t('Case data will be available once time estimation is complete')}</div>
      ) : (
        <Tabs
          tabslist={tabslist}
          // switchMode={messageModeList}
          onModeIndexChange={(index) => setMessageModeIndex(index)}
          onIndexChange={(index) => {
            setTabIndex(index);
            clearAllSelectedMessages();
            setSearchQuery(null);
            keywordsRef.current = '';
            setPlatforms([]);
            setSourceGroups([]);
            setLanguages([]);
            setSources([]);
            setCountries([]);
            setSentiment([]);
            setContentTypes(defaultContentTypes);
            setMessagesStatus([]);
            setShowOnlyFavorites(false);
            setSearchQuery(null);
            setStartDate(null);
            setEndDate(null);
            setSearchQuery(null);
            setShowOnlyBots(false);
            setDiscreditations([]);
            setAffiliationCountries([]);
            setFeeds([]);
            setGeoGroup([]);
            setLanguagesExclude(false);
            setCountriesExclude(false);
            setSourceGroupsExclude(false);
            setSourcesExclude(false);
            setAffiliationCountriesExclude(false);
            setFeedsExclude(false);
            setGeoGroupExclude(false);
            setBotsExclude(false);
            setDiscreditationsExclude(false);

            toggleTab();
            if (
              (tabslist[index].title === 'Stories' ||
                tabslist[index].title === 'Сюжети' ||
                tabslist[index].title === 'Opinions' ||
                tabslist[index].title === 'Точки зору') &&
              !deduplication
            ) {
              toggleDeduplication(true);
              setSorting('-messages_count');
              dispatch({type: 'IS_BULK_TRANSLATION', isBulkTranslation: false});
              dispatch({type: 'BULK_TRANSLATION_LOADING', isBulkTranslationLoading: false});
              cancelTranslateAll();
              applySearch(false);
              setCursor(null);
              setPage(1);
            } else if (
              tabslist[index].title !== 'Stories' &&
              tabslist[index].title !== 'Сюжети' &&
              tabslist[index].title !== 'Opinions' &&
              tabslist[index].title !== 'Точки зору' &&
              deduplication
            ) {
              toggleDeduplication(false);
              setSorting('-date_publicated');
              dispatch({type: 'IS_BULK_TRANSLATION', isBulkTranslation: false});
              dispatch({type: 'BULK_TRANSLATION_LOADING', isBulkTranslationLoading: false});
              cancelTranslateAll();
              applySearch(false);
              setCursor(null);
              setPage(1);
            }
          }}
          // activeMode={messageModeIndex}
        />
      )}

      <Modal
        isVisible={approveConfirmModal.isActive}
        title={
          approveConfirmModal.status === 'READ'
            ? t('Approve')
            : t('Mark as unread')
        }
        content={
          <p>
            {approveConfirmModal.status === 'READ'
              ? (approveConfirmModal.ids &&
                  approveConfirmModal.ids.length > 1) ||
                areAllMessagesSelected
                ? t('Are you sure you want to approve these publications?')
                : t('Are you sure you want to approve this publication?')
              : (approveConfirmModal.ids &&
                  approveConfirmModal.ids.length > 1) ||
                areAllMessagesSelected
              ? t('Are you sure you want to mark these publications as unread?')
              : t('Are you sure you want to mark this publication as unread?')}
          </p>
        }
        footer={
          <>
            <button
              className="btn-primary"
              onClick={() => {
                if (areAllMessagesSelected) {
                  handleAllMessages(
                    approveConfirmModal.narrative,
                    searchQuery,
                    messagesStatus,
                    showOnlyFavorites,
                    startDate,
                    endDate,
                    platforms,
                    sourceGroups,
                    languages,
                    sources,
                    countries,
                    sentiment,
                    contentTypes,
                    showOnlyBots,
                    discreditations,
                    affiliationCountries,
                    feeds,
                    geoGroup,
                    languagesExclude,
                    countriesExclude,
                    sourceGroupsExclude,
                    sourcesExclude,
                    affiliationCountriesExclude,
                    feedsExclude,
                    geoGroupExclude,
                    botsExclude,
                    discreditationsExclude,
                    tags,
                    approveConfirmModal.status,
                  ).then(() => {
                    dispatch({ type: 'PATCH_MESSAGES' });
                    getMessages(
                      narrative,
                      searchQuery,
                      sorting,
                      messagesStatus,
                      deduplication,
                      page,
                      showOnlyFavorites,
                      startDate,
                      endDate,
                      platforms,
                      sourceGroups,
                      isValidQuery,
                      languages,
                      sources,
                      countries,
                      sentiment,
                      contentTypes,
                      showOnlyBots,
                      discreditations,
                      affiliationCountries,
                      feeds,
                      geoGroup,
                      languagesExclude,
                      countriesExclude,
                      sourceGroupsExclude,
                      sourcesExclude,
                      affiliationCountriesExclude,
                      feedsExclude,
                      geoGroupExclude,
                      botsExclude,
                      discreditationsExclude,
                      tags,
                      tagsExclude,
                    );
                  });
                  selectAllModalMessage();
                } else {
                  patchMessages(
                    approveConfirmModal.narrative,
                    approveConfirmModal.status,
                    approveConfirmModal.ids,
                    deduplication,
                  ).then(() => {
                    dispatch({ type: 'PATCH_MESSAGES' });
                    getMessages(
                      narrative,
                      searchQuery,
                      sorting,
                      messagesStatus,
                      deduplication,
                      page,
                      showOnlyFavorites,
                      startDate,
                      endDate,
                      platforms,
                      sourceGroups,
                      isValidQuery,
                      languages,
                      sources,
                      countries,
                      sentiment,
                      contentTypes,
                      showOnlyBots,
                      discreditations,
                      affiliationCountries,
                      feeds,
                      geoGroup,
                      languagesExclude,
                      countriesExclude,
                      sourceGroupsExclude,
                      sourcesExclude,
                      affiliationCountriesExclude,
                      feedsExclude,
                      geoGroupExclude,
                      botsExclude,
                      discreditationsExclude,
                      tags,
                      tagsExclude,
                    );
                  });
                }
                setApproveConfirmModal({
                  isActive: false,
                  narrative: null,
                  status: null,
                  ids: null,
                });
              }}
            >
              {approveConfirmModal.status === 'READ'
                ? t('Approve')
                : t('Confirm')}
            </button>
            <button
              className="btn-reset"
              onClick={() =>
                setApproveConfirmModal({
                  isActive: false,
                  narrative: null,
                  status: null,
                  ids: null,
                })
              }
            >
              {t('Cancel')}
            </button>
          </>
        }
        onClose={() =>
          setApproveConfirmModal({
            isActive: false,
            narrative: null,
            status: null,
            id: null,
          })
        }
      />
      <Modal
        isVisible={deleteConfirmModal.isActive}
        title={
          deleteConfirmModal.status === 'DELETED' ? t('Restore') : t('Delete')
        }
        content={
          <p>
            {deleteConfirmModal.status !== 'DELETED'
              ? (deleteConfirmModal.ids && deleteConfirmModal.ids.length > 1) ||
                areAllMessagesSelected
                ? t('Are you sure you want to delete these publications?')
                : t(
                    'Are you sure you want to delete this publication from a narrative?',
                  )
              : (deleteConfirmModal.ids && deleteConfirmModal.ids.length > 1) ||
                areAllMessagesSelected
              ? t('Are you sure you want to restore these publications?')
              : t('Are you sure you want to restore this publication?')}
          </p>
        }
        footer={
          <>
            <button
              className="btn-danger"
              onClick={() => {
                if (deleteConfirmModal.status === 'DELETED') {
                  if (areAllMessagesSelected) {
                    handleAllMessages(
                      deleteConfirmModal.narrative,
                      searchQuery,
                      messagesStatus,
                      showOnlyFavorites,
                      startDate,
                      endDate,
                      platforms,
                      sourceGroups,
                      languages,
                      sources,
                      countries,
                      sentiment,
                      contentTypes,
                      showOnlyBots,
                      discreditations,
                      affiliationCountries,
                      feeds,
                      geoGroup,
                      languagesExclude,
                      countriesExclude,
                      sourceGroupsExclude,
                      sourcesExclude,
                      affiliationCountriesExclude,
                      feedsExclude,
                      geoGroupExclude,
                      botsExclude,
                      discreditationsExclude,
                      tags,
                      'NEW',
                    ).then(() => {
                      dispatch({ type: 'PATCH_MESSAGES' });
                      getMessages(
                        narrative,
                        searchQuery,
                        sorting,
                        messagesStatus,
                        deduplication,
                        page,
                        showOnlyFavorites,
                        startDate,
                        endDate,
                        platforms,
                        sourceGroups,
                        isValidQuery,
                        languages,
                        sources,
                        countries,
                        sentiment,
                        contentTypes,
                        showOnlyBots,
                        discreditations,
                        affiliationCountries,
                        feeds,
                        geoGroup,
                        languagesExclude,
                        countriesExclude,
                        sourceGroupsExclude,
                        sourcesExclude,
                        affiliationCountriesExclude,
                        feedsExclude,
                        geoGroupExclude,
                        botsExclude,
                        discreditationsExclude,
                        tags,
                        tagsExclude,
                      );
                    });
                    setDeleteConfirmModal({
                      isActive: false,
                      narrative: null,
                      status: null,
                      ids: null,
                    });
                    selectAllModalMessage();
                  } else {
                    patchMessages(
                      deleteConfirmModal.narrative,
                      'NEW',
                      deleteConfirmModal.ids,
                      deduplication,
                    ).then(() => {
                      dispatch({ type: 'PATCH_MESSAGES' });
                      getMessages(
                        narrative,
                        searchQuery,
                        sorting,
                        messagesStatus,
                        deduplication,
                        page,
                        showOnlyFavorites,
                        startDate,
                        endDate,
                        platforms,
                        sourceGroups,
                        isValidQuery,
                        languages,
                        sources,
                        countries,
                        sentiment,
                        contentTypes,
                        showOnlyBots,
                        discreditations,
                        affiliationCountries,
                        feeds,
                        geoGroup,
                        languagesExclude,
                        countriesExclude,
                        sourceGroupsExclude,
                        sourcesExclude,
                        affiliationCountriesExclude,
                        feedsExclude,
                        geoGroupExclude,
                        botsExclude,
                        discreditationsExclude,
                        tags,
                        tagsExclude,
                      );
                    });
                    setDeleteConfirmModal({
                      isActive: false,
                      narrative: null,
                      status: null,
                      ids: null,
                    });
                  }
                } else {
                  if (areAllMessagesSelected) {
                    handleAllMessages(
                      deleteConfirmModal.narrative,
                      searchQuery,
                      messagesStatus,
                      showOnlyFavorites,
                      startDate,
                      endDate,
                      platforms,
                      sourceGroups,
                      languages,
                      sources,
                      countries,
                      sentiment,
                      contentTypes,
                      showOnlyBots,
                      discreditations,
                      affiliationCountries,
                      feeds,
                      geoGroup,
                      languagesExclude,
                      countriesExclude,
                      sourceGroupsExclude,
                      sourcesExclude,
                      affiliationCountriesExclude,
                      feedsExclude,
                      geoGroupExclude,
                      botsExclude,
                      discreditationsExclude,
                      tags,
                      'DELETED',
                    ).then(() => {
                      dispatch({ type: 'PATCH_MESSAGES' });
                      getMessages(
                        narrative,
                        searchQuery,
                        sorting,
                        messagesStatus,
                        deduplication,
                        page,
                        showOnlyFavorites,
                        startDate,
                        endDate,
                        platforms,
                        sourceGroups,
                        isValidQuery,
                        languages,
                        sources,
                        countries,
                        sentiment,
                        contentTypes,
                        showOnlyBots,
                        discreditations,
                        affiliationCountries,
                        feeds,
                        geoGroup,
                        languagesExclude,
                        countriesExclude,
                        sourceGroupsExclude,
                        sourcesExclude,
                        affiliationCountriesExclude,
                        feedsExclude,
                        geoGroupExclude,
                        botsExclude,
                        discreditationsExclude,
                        tags,
                        tagsExclude,
                      );
                    });
                    setDeleteConfirmModal({
                      isActive: false,
                      narrative: null,
                      status: null,
                      ids: null,
                    });
                    selectAllModalMessage();
                  } else {
                    deleteMessages(
                      deleteConfirmModal.narrative,
                      deleteConfirmModal.ids,
                      deduplication,
                    ).then(() => {
                      dispatch({ type: 'DELETE_MESSAGES' });
                      getMessages(
                        narrative,
                        searchQuery,
                        sorting,
                        messagesStatus,
                        deduplication,
                        page,
                        showOnlyFavorites,
                        startDate,
                        endDate,
                        platforms,
                        sourceGroups,
                        isValidQuery,
                        languages,
                        sources,
                        countries,
                        sentiment,
                        contentTypes,
                        showOnlyBots,
                        discreditations,
                        affiliationCountries,
                        feeds,
                        geoGroup,
                        languagesExclude,
                        countriesExclude,
                        sourceGroupsExclude,
                        sourcesExclude,
                        affiliationCountriesExclude,
                        feedsExclude,
                        geoGroupExclude,
                        botsExclude,
                        discreditationsExclude,
                        tags,
                        tagsExclude,
                      );
                    });
                    setDeleteConfirmModal({
                      isActive: false,
                      narrative: null,
                      status: null,
                      ids: null,
                    });
                  }
                }
              }}
            >
              {deleteConfirmModal.status === 'DELETED'
                ? t('Restore')
                : t('Delete')}
            </button>
            <button
              className="btn-reset"
              onClick={() =>
                setDeleteConfirmModal({
                  isActive: false,
                  narrative: null,
                  status: null,
                  ids: null,
                })
              }
            >
              {t('Cancel')}
            </button>
          </>
        }
        onClose={() =>
          setDeleteConfirmModal({
            isActive: false,
            narrative_id: null,
            status: null,
            id: null,
          })
        }
      />

      <Modal
        isVisible={mergeStoriesModal.isActive}
        title={t('Merge stories')}
        doNotCloseOnOutsideClick={true}
        content={
          mergeStoriesModal.story_ids.length < 2 ? (
            <p className={styles.errorStoriesMerge}>
              {t('Please select at least 2 stories to merge')}
            </p>
          ) : (
            <div className={styles.mergeStoriesModal}>
              {mergeStoriesModal.error ? (
                <p className={styles.errorStoriesMerge}>
                  {mergeStoriesModal.error}
                </p>
              ) : (
                ''
              )}
              <div className={styles.formElement}>
                <label htmlFor="new-story-summary">
                  {t('Description')}&#42;
                </label>
                <input
                  type="text"
                  id="new-story-summary"
                  value={mergeStoriesModal.new_summary}
                  required
                  onChange={(e) =>
                    setMergeStoriesModal({
                      ...mergeStoriesModal,
                      new_summary: e.target.value,
                    })
                  }
                />
              </div>
            </div>
          )
        }
        footer={
          <>
            {mergeStoriesModal.story_ids.length < 2 ? (
              ''
            ) : (
              <button
                className="btn-primary"
                disabled={mergeStoriesModal.isLoading}
                onClick={() => {
                  setMergeStoriesModal({
                    ...mergeStoriesModal,
                    isLoading: true,
                  });

                  API.fetch(
                    'POST',
                    `/API/v1/narrative_stories/${narrative.id}/merge`,
                    null,
                    {
                      story_ids: mergeStoriesModal.story_ids,
                      new_summary: mergeStoriesModal.new_summary,
                    },
                  )
                    .then(() => {
                      setMergeStoriesModal({
                        isActive: false,
                        story_ids: [],
                        new_summary: '',
                        isLoading: false,
                        error: null,
                      });

                      clearSelectedMessages();

                      getStories(
                        narrative,
                        searchQuery,
                        startDate,
                        endDate,
                        messagesStatus,
                        sorting,
                        page,
                      );
                    })
                    .catch(() => {
                      setMergeStoriesModal({
                        ...mergeStoriesModal,
                        isLoading: false,
                        error: t('Something went wrong. Please, try again...'),
                      });
                    });
                }}
              >
                {mergeStoriesModal.isLoading ? <LoaderSmall /> : t('Merge')}
              </button>
            )}
            <button
              className="btn-reset"
              disabled={mergeStoriesModal.isLoading}
              onClick={() =>
                setMergeStoriesModal({
                  isActive: false,
                  story_ids: [],
                  new_summary: '',
                  isLoading: false,
                  error: null,
                })
              }
            >
              {t('Cancel')}
            </button>
          </>
        }
        onClose={() =>
          setMergeStoriesModal({
            isActive: false,
            story_ids: [],
            new_summary: '',
            isLoading: false,
            error: null,
          })
        }
      />

      {saveAsThreatModal.isOpen ? (
        <ThreatEditModal
          isOpen={saveAsThreatModal.isOpen}
          title={
            isUcbiUser ? t('Share as an instance') : t('Share as a threat')
          }
          threat={saveAsThreatModal.narrative}
          onChange={(bool) =>
            setSaveAsThreatModal({ isOpen: bool, threat: null })
          }
          create={true}
          handleThreat={({
            threat,
            threatName,
            threatDescription,
            caseType,
            incidentType,
            topics,
            geoScope,
            narratives,
            campaigns,
            counterEntities,
            reviewStatus,
            declineReason,
            organizations,
            isCreatedByUcbiReporter,
            threatPublicNameEn,
            threatPublicNameUk,
            threatPublicDescriptionEn,
            threatPublicDescriptionUk,
            threatIsPublic,
          }) =>
            handleSaveAsThreat({
              narrative: threat,
              threatName,
              threatDescription,
              caseType,
              incidentType,
              topics,
              geoScope,
              narratives,
              campaigns,
              counterEntities,
              reviewStatus,
              declineReason,
              organizations,
              isCreatedByUcbiReporter,
              threatPublicNameEn,
              threatPublicNameUk,
              threatPublicDescriptionEn,
              threatPublicDescriptionUk,
              threatIsPublic,
            })
          }
          message={t(
            'You are about to share your case as a threat. Once submitted, it will become visible to other users of our system and you won’t be able to edit it anymore. Please, double-check that the content of the case and its settings are accurate.',
          )}
        />
      ) : (
        ''
      )}

      {editThreatModal.isOpen ? (
        <ThreatEditModal
          isOpen={editThreatModal.isOpen}
          title={isUcbiUser ? t('Edit the instance') : t('Edit the threat')}
          threat={editThreatModal.threat}
          isAdmin={currentUser?.is_super_admin}
          onChange={(bool) =>
            setEditThreatModal({ isOpen: bool, threat: null })
          }
          handleThreat={({
            threat,
            threatName,
            threatDescription,
            caseType,
            incidentType,
            topics,
            geoScope,
            narratives,
            campaigns,
            counterEntities,
            reviewStatus,
            declineReason,
            organizations,
            isCreatedByUcbiReporter,
            threatPublicNameEn,
            threatPublicNameUk,
            threatPublicDescriptionEn,
            threatPublicDescriptionUk,
            threatIsPublic,
          }) =>
            patchThreat({
              threat,
              threatName,
              threatDescription,
              caseType,
              incidentType,
              topics,
              geoScope,
              narratives,
              campaigns,
              counterEntities,
              reviewStatus,
              declineReason,
              organizations,
              isCreatedByUcbiReporter,
              threatPublicNameEn,
              threatPublicNameUk,
              threatPublicDescriptionEn,
              threatPublicDescriptionUk,
              threatIsPublic,
              workspaceId: currentUser.is_super_admin
                ? threat.workspace_id
                : null,
            })
          }
        />
      ) : (
        ''
      )}

      {addToThreatModal ? (
        <ThreatEditModal
          isOpen={addToThreatModal}
          title={t('Add to a threat')}
          threat={narrative}
          isAdmin={currentUser?.is_super_admin}
          onChange={(bool) => setAddToThreatModal(bool)}
          handleThreat={({
            threat,
            threatName,
            threatDescription,
            caseType,
            incidentType,
            topics,
            geoScope,
            narratives,
            campaigns,
            counterEntities,
            reviewStatus,
            declineReason,
            organizations,
            isCreatedByUcbiReporter,
            threatPublicNameEn,
            threatPublicNameUk,
            threatPublicDescriptionEn,
            threatPublicDescriptionUk,
            threatIsPublic,
          }) =>
            saveToThreat(
              narrativeId,
              threatName,
              threatDescription,
              caseType,
              incidentType,
              topics,
              geoScope,
              narratives,
              campaigns,
              counterEntities,
              reviewStatus,
              declineReason,
              organizations,
              isCreatedByUcbiReporter,
              threatPublicNameEn,
              threatPublicNameUk,
              threatPublicDescriptionEn,
              threatPublicDescriptionUk,
              threatIsPublic,
            )
          }
        />
      ) : (
        ''
      )}

      <Modal
        isVisible={saveAsNarrativeModal.isOpen}
        title={t('Save as a case')}
        content={
          <p>{t('Are you sure you want to save the threat as a case?')}</p>
        }
        footer={
          <>
            <button
              className="btn-primary"
              onClick={() => {
                saveAsNarrative(saveAsNarrativeModal.threat);
                setSaveAsNarrativeModal({ isOpen: false, threat: null });
              }}
            >
              {t('Save')}
            </button>
            <button
              className="btn-reset"
              onClick={() =>
                setSaveAsNarrativeModal({ isOpen: false, threat: null })
              }
            >
              {t('Cancel')}
            </button>
          </>
        }
        onClose={() => setSaveAsNarrativeModal({ isOpen: false, threat: null })}
      />

      {errorModal ? (
        <Modal
          isVisible={errorModal}
          title={t('The case does not meet requirements for sharing')}
          className={styles.errorModal}
          content={
            <div className={styles.errorModalText}>
              <div>
                {t(
                  `This case can’t be shared as a threat because it doesn't meet the necessary criteria. Please update the case according to the following requirements:`,
                )}
              </div>
              <ul>
                {/* {!isNarrativeValid.description 
              ? <li>
                <span>{t('Description Required: ')}</span> 
                {t(`Please provide a description for this case.`)}</li>
              : ''} */}
                <li>
                  <span>{t('Minimum 3 Confirmed Publications: ')}</span>
                  {t(
                    `To share a case as a threat, it should contain at least 3 confirmed publications.`,
                  )}
                </li>

                {/* {!isNarrativeValid.allMessages 
              ? <li>
                  <span>{t('All Publications Confirmation: ')}</span> 
                  {t(`Ensure that all publications in the case are either confirmed or deleted.`)}</li> 
              : ''} */}
              </ul>

              <div>
                {isUcbiUser
                  ? t(
                      `Once these requirements are met, you can click the 'Share as an instance' button again, and your case will be shared successfully.`,
                    )
                  : t(
                      `Once these requirements are met, you can click the 'Share as a threat' button again, and your case will be shared successfully.`,
                    )}
              </div>
            </div>
          }
          footer={
            <>
              <button
                className="btn-reset"
                onClick={() => setErrorModal(false)}
              >
                {t('Close')}
              </button>
            </>
          }
          onClose={() => setErrorModal(false)}
        />
      ) : (
        ''
      )}
      {currentUser?.is_super_admin === true ? (
        <CopyNarrativeToWorkspacePopup
          isVisible={copyNarrativeModal.isActive}
          narrativeId={narrative.id}
          onClose={() => setCopyNarrativeModal({ isActive: false })}
        />
      ) : (
        ''
      )}

      {addToNarrativeModal ? (
        <AddToNarrativeModal
          isOpen={addToNarrativeModal}
          messages={selectedMessages}
          deduplication={deduplication}
          caseId={narrative.id}
          areAllMessagesSelected={areAllMessagesSelected}
          searchQuery={searchQuery}
          messagesStatus={messagesStatus}
          showOnlyFavorites={showOnlyFavorites}
          startDate={startDate}
          endDate={endDate}
          platforms={platforms}
          sourceGroups={sourceGroups}
          languages={languages}
          sources={sources}
          countries={countries}
          sentiment={sentiment}
          contentTypes={contentTypes}
          showOnlyBots={showOnlyBots}
          botsExclude={botsExclude}
          discreditations={discreditations}
          affiliationCountries={affiliationCountries}
          feeds={feeds}
          geoGroup={geoGroup}
          languagesExclude={languagesExclude}
          countriesExclude={countriesExclude}
          sourceGroupsExclude={sourceGroupsExclude}
          sourcesExclude={sourcesExclude}
          affiliationCountriesExclude={affiliationCountriesExclude}
          feedsExclude={feedsExclude}
          geoGroupExclude={geoGroupExclude}
          highRiskExclude={discreditationsExclude}
          onChange={(bool) => {
            setAddToNarrativeModal(bool);
          }}
        />
      ) : (
        ''
      )}

      {addToSourceGroupMadal ? (
        <AddToSourceGroupModal
          isOpen={addToSourceGroupMadal}
          messages={selectedMessages}
          deduplication={deduplication}
          areAllMessagesSelected={areAllMessagesSelected}
          searchQuery={searchQuery}
          messagesStatus={messagesStatus}
          showOnlyFavorites={showOnlyFavorites}
          startDate={startDate}
          endDate={endDate}
          platforms={platforms}
          sourceGroups={sourceGroups}
          languages={languages}
          sourcesFilter={sources}
          countries={countries}
          sentiment={sentiment}
          contentTypes={contentTypes}
          showOnlyBots={showOnlyBots}
          botsExclude={botsExclude}
          discreditations={discreditations}
          affiliationCountries={affiliationCountries}
          feeds={feeds}
          geoGroup={geoGroup}
          languagesExclude={languagesExclude}
          countriesExclude={countriesExclude}
          sourceGroupsExclude={sourceGroupsExclude}
          sourcesExclude={sourcesExclude}
          affiliationCountriesExclude={affiliationCountriesExclude}
          feedsExclude={feedsExclude}
          geoGroupExclude={geoGroupExclude}
          highRiskExclude={discreditationsExclude}
          narrative={narrative}
          onChange={(bool) => {
            setAddToSourceGroupModal(bool);
          }}
        />
      ) : (
        ''
      )}

      <ExportReportPopup
        isVisible={exportModal.isActive}
        onClose={() => setExportModal({ isActive: false, id: null })}
        narrativeId={narrative.id}
      />
    </div>
  );
}
